import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { formatAutoPayAmount } from '../../autopay-format.utils';
import { ConfigService } from '@aiwyn/config';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  AddEditAutopayDialogComponent,
  AddEditAutopayDialogData,
} from '../../modals/add-edit-autopay-dialog/add-edit-autopay-dialog.component';
import { combineLatest, Observable, Subject } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import {
  PaymentMethodIsNotFromUserComponent,
} from '../../modals/payment-method-is-not-from-user/payment-method-is-not-from-user.component';
import { Store } from '@ngxs/store';
import { ClearScheduledPayments, GetPaymentPlans } from '../state';
import { getLoadedResults, ignoreFalsy, isLoaded, LoadableStatus, PillColor } from '@aiwyn/shared';
import { DeleteAutoPayDialogComponent } from '../delete-auto-pay-dialog/delete-auto-pay-dialog.component';
import { CommonSettingsState, GetAutoPayClients } from '@aiwyn/common/settings';
import { AutoPay, PaymentMethodType, PaymentPlanUi } from '@aiwyn/api';
import { AbstractPaymentMethod, PaymentPlatformService } from '@aiwyn/common/payments';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AutopayEligibilityService } from '@aiwyn/services';

interface AutoPayPill {
  color: PillColor;
  value: AutoPayPillValue;
}

type AutoPayPillValue = 'Enabled' | 'Not enabled' | 'Not supported';

@Component({
  selector: 'app-auto-pay-settings',
  templateUrl: './auto-pay-settings.component.html',
  styleUrls: ['./auto-pay-settings.component.scss'],
  standalone: false,
})
export class AutoPaySettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  autopayEligibilityService = inject(AutopayEligibilityService);

  private readonly _unsubscribe$: Subject<void> = new Subject<void>();
  readonly PaymentMethodType = PaymentMethodType;

  @Input() paymentPlanUis: PaymentPlanUi[];

  @ViewChild(MatSort) sort: MatSort;

  // Table columns
  columnsToDisplay = ['account', 'enabled', 'day', 'method', 'amount', 'actions'];
  dataSource: MatTableDataSource<AutoPay & { pill: AutoPayPill }> = new MatTableDataSource([]);

  hasError$ = this.store.select(CommonSettingsState.getAutoPayClientsStatus).pipe(
    map(status => status === LoadableStatus.ERROR),
  );

  autoPayClientMonitor$ = combineLatest([
    getLoadedResults(this.store.select(CommonSettingsState.getAutoPayClientsLoadable)),
    this.paymentPlatformService.getSavedPaymentMethods(),
    this.store.select(CommonSettingsState.getClientCurrenciesStatus).pipe(
      isLoaded(true),
      switchMap(_ => this.store.selectOnce(CommonSettingsState.getClientCurrencies)),
    ),
  ]).pipe(
    tap(([autoPayClients, savedPaymentMethods, clientCurrencies]) => {
      const processedAutoPayClients = autoPayClients.map(autoPayClientWithoutCurrency => {
        const autoPayClient: AutoPay = {
          ...autoPayClientWithoutCurrency,
          isClientNonUsd: clientCurrencies[autoPayClientWithoutCurrency.clientId]?.data?.code !== 'USD',
        };
        const paymentMethodId = autoPayClientWithoutCurrency.method?.paymentMethodId;

        const autoPayPill: AutoPayPill = {
          color: AutoPaySettingsComponent.getPillColor(autoPayClient),
          value: AutoPaySettingsComponent.getPillValue(autoPayClient),
        };

        if (paymentMethodId == undefined) {
          return { ...autoPayClient, pill: autoPayPill };
        }

        return {
          ...autoPayClient,
          pill: autoPayPill,
          isPaymentMethodFromUser: savedPaymentMethods.some(({ id }) => id === paymentMethodId),
        };
      });

      this.dataSource.data = processedAutoPayClients;
    }),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    public readonly configService: ConfigService,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private readonly activatedRoute: ActivatedRoute,
    private readonly currencyPipe: CurrencyPipe,
  ) { }

  formatAmount(amount: number): string {
    return formatAutoPayAmount(this.currencyPipe, amount);
  }

  ngOnInit(): void {
    this.autoPayClientMonitor$.subscribe();

    // Check the AdminSsoFields to see if the dialog should be launched
    combineLatest([
      getLoadedResults(this.store.select(CommonSettingsState.getAutoPayClientsLoadable)),
      this.activatedRoute.queryParams.pipe(
        map(queryParams => +queryParams?.['paymentPlanId']),
      ),
    ]).pipe(
      map(([autoPayClients, paymentPlanId]) => autoPayClients.find(({ autoPayId }) => autoPayId === paymentPlanId)),
      ignoreFalsy(),
      switchMap(autoPayDisplay => this.openAddEditDialog(autoPayDisplay)),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = AutoPaySettingsComponent.sortingDataAccessor;
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  static getPillColor(autoPayDisplay: AutoPay): PillColor {
    if (autoPayDisplay.isClientNonUsd) {
      return 'gray';
    }

    return autoPayDisplay.active ? 'green' : 'red';
  }

  static getPillValue(autoPayDisplay: AutoPay): AutoPayPillValue {
    if (autoPayDisplay.isClientNonUsd) {
      return 'Not supported';
    }

    return autoPayDisplay.active ? 'Enabled' : 'Not enabled';
  }

  isDirectDebit(paymentMethodType: PaymentMethodType): boolean {
    return AbstractPaymentMethod.isDirectDebit(paymentMethodType);
  }

  addEditAutoPay(autoPayDisplay: AutoPay): void {
    if (autoPayDisplay.isClientNonUsd) {
      return;
    }

    this.openAddEditDialog(autoPayDisplay).subscribe();
  }

  openAddEditDialog(autoPayDisplay: AutoPay): Observable<any> {
    // used in dialog to determine recurring plans to deactivate and whether container should update recurring plans data
    const recurringPaymentPlans = this.paymentPlanUis
      .filter(paymentPlanUi => paymentPlanUi.client.id === autoPayDisplay.clientId)
      .map(({ paymentPlan }) => paymentPlan);

    return this.dialog.open<AddEditAutopayDialogComponent, AddEditAutopayDialogData>(
      AddEditAutopayDialogComponent,
      {
        data: {
          isEnabled: autoPayDisplay.active,
          account: {
            id: autoPayDisplay.clientId,
            name: autoPayDisplay.account,
          },
          didCreate: !autoPayDisplay.active || autoPayDisplay.isPaymentMethodFromUser !== false,
          autoPay: autoPayDisplay.autoPayId ? autoPayDisplay : undefined,
          recurringPaymentPlans,
        },
      },
    ).afterClosed().pipe(
      take(1),
      ignoreFalsy(),
      tap(_ => {
        this.store.dispatch(
          [new GetAutoPayClients(true), new ClearScheduledPayments()]
            .concat(recurringPaymentPlans.length > 0 ? new GetPaymentPlans(true) : [])
        );
      }),
      takeUntil(this._unsubscribe$),
    );
  }

  openDisableDialog(autoPayDisplay: AutoPay): void {
    this.dialog.open(DeleteAutoPayDialogComponent, { data: autoPayDisplay });
  }

  launchPaymentMethodIsNotFromUserInfoModal(): void {
    this.dialog.open(PaymentMethodIsNotFromUserComponent);
  }

  static sortingDataAccessor(record: AutoPay, columnName: string): string | number {
    switch (columnName) {
      case 'account':
        return record.account
      case 'enabled':
        return record.active ? 1 : 0;
      // Sort by day field in date
      case 'day':
        return record.active && record.startDate ? record.startDate.split('-')[2] : 0;
      // Construct card string in order to sort
      case 'method':
        return record.active && record.method ? record.method.cardType + record.method.last4 : '';
      case 'amount':
        // If the record is active return the amount, or the INT MAX to get 'Paid in full' to float to the top
        if (record.active) {
          return record.amount || Number.MAX_VALUE
        }
        return 0;
      // sOrTiNg AcTiOnS? sIlLy GoOsE
      case 'actions':
        return 1;
    }
  }
}
